import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { DailyMetricPoint } from "./wearableMetricAggregation";

interface WearableMetricLineChartProps {
  color: string;
  unit: string;
  dailyPoints: DailyMetricPoint[];
  rangeDays: number;
}

interface ChartRow {
  dateLabel: string;
  value: number | null;
  displayValue: string;
}

function CustomTooltip({
  active,
  payload,
  unit,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ChartRow }>;
  unit: string;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {row.dateLabel}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        {row.displayValue}
        {unit && row.value !== null ? ` ${unit}` : ""}
      </Typography>
    </Box>
  );
}

export function WearableMetricLineChart({
  color,
  unit,
  dailyPoints,
  rangeDays,
}: WearableMetricLineChartProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const chartData: ChartRow[] = dailyPoints.map((point) => ({
    dateLabel: point.dateLabel,
    value: point.value,
    displayValue: point.displayValue,
  }));

  const hasAnyData = chartData.some((row) => row.value !== null);

  if (rangeDays === 1) {
    return (
      <Box
        sx={{
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px dashed",
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 2 }}>
          Selecione um período maior para ver tendência
        </Typography>
      </Box>
    );
  }

  if (!hasAnyData) {
    return (
      <Box
        sx={{
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Sem dados no período selecionado
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: 260, minHeight: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: isMobile ? 10 : 12 }}
            interval={isMobile ? "preserveStartEnd" : 0}
            angle={isMobile ? -35 : 0}
            textAnchor={isMobile ? "end" : "middle"}
            height={isMobile ? 48 : 32}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            width={48}
            tickFormatter={(value: number) =>
              value >= 1000 ? `${(value / 1000).toFixed(1)}k` : `${value}`
            }
          />
          <Tooltip content={<CustomTooltip unit={unit} />} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2.5}
            dot={{ r: 4, fill: color, strokeWidth: 0 }}
            activeDot={{ r: 6, fill: color }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
